import { gql } from '@apollo/client'

export const CREATE_WEEKLY_PLAN_MUTATION = gql`
  mutation createWeeklyPlan($attributes: WeeklyPlanInput!) {
    createWeeklyPlan(attributes: $attributes) {
      id
      calendarWeek
      year
      status
    }
  }
`

export const UPDATE_WEEKLY_PLAN_STATUS_MUTATION = gql`
  mutation updateWeeklyPlanStatus($id: ID!, $status: WeeklyPlanStatusEnum!) {
    updateWeeklyPlanStatus(id: $id, status: $status) {
      id
      status
    }
  }
`

export const UPSERT_WEEKLY_AVAILABILITY_MUTATION = gql`
  mutation upsertWeeklyAvailability(
    $weeklyPlanId: ID!
    $userId: ID!
    $confirmationStatus: ConfirmationStatusEnum
    $notes: String
    $slots: [AvailabilitySlotInput!]
  ) {
    upsertWeeklyAvailability(
      weeklyPlanId: $weeklyPlanId
      userId: $userId
      confirmationStatus: $confirmationStatus
      notes: $notes
      slots: $slots
    ) {
      id
      confirmationStatus
      notes
      totalAssignments
      totalHours
      availabilitySlots {
        id
        weekday
        startTime
        endTime
        slotType
        durationHours
      }
    }
  }
`

export const DELETE_AVAILABILITY_SLOT_MUTATION = gql`
  mutation deleteAvailabilitySlot($id: ID!) {
    deleteAvailabilitySlot(id: $id) {
      id
    }
  }
`

export const COPY_AVAILABILITY_FROM_WEEK_MUTATION = gql`
  mutation copyAvailabilityFromWeek(
    $targetWeeklyPlanId: ID!
    $sourceWeeklyPlanId: ID
    $userIds: [ID!]
  ) {
    copyAvailabilityFromWeek(
      targetWeeklyPlanId: $targetWeeklyPlanId
      sourceWeeklyPlanId: $sourceWeeklyPlanId
      userIds: $userIds
    ) {
      weeklyAvailabilities {
        id
      }
    }
  }
`

export const COPY_AVAILABILITY_FROM_TEMPLATE_MUTATION = gql`
  mutation copyAvailabilityFromTemplate($weeklyPlanId: ID!, $userIds: [ID!]) {
    copyAvailabilityFromTemplate(weeklyPlanId: $weeklyPlanId, userIds: $userIds) {
      weeklyAvailabilities {
        id
        confirmationStatus
        availabilitySlots {
          id
          weekday
          startTime
          endTime
          slotType
        }
      }
    }
  }
`

export const UPSERT_MEDIATOR_PROFILE_MUTATION = gql`
  mutation upsertMediatorProfile(
    $userId: ID!
    $attributes: MediatorProfileInput!
    $mediatorTagIds: [ID!]
    $caseTargetValidFrom: ISO8601Date
  ) {
    upsertMediatorProfile(
      userId: $userId
      attributes: $attributes
      mediatorTagIds: $mediatorTagIds
      caseTargetValidFrom: $caseTargetValidFrom
    ) {
      id
      distributionCategory
      preferredTopics
      acceptableTopics
      excludedTopics
      minCasesPerWeek
      maxCasesPerWeek
      hoursPerWeek
      workingDaysPerWeek
      canDoPresence
      isSpecialist
      specialNotes
      upcomingCaseTargets {
        validFrom
        minCasesPerWeek
        maxCasesPerWeek
        hoursPerWeek
      }
      mediatorTags {
        id
        name
        slug
      }
    }
  }
`

export const SUBMIT_TEAM_PLAN_MUTATION = gql`
  mutation submitTeamPlan($weeklyPlanId: ID!, $teamId: ID!) {
    submitTeamPlan(weeklyPlanId: $weeklyPlanId, teamId: $teamId) {
      id
      status
      submittedAt
      submittedBy {
        id
        firstname
        lastname
      }
    }
  }
`

export const UPSERT_DAILY_CASE_QUOTA_OVERRIDE_MUTATION = gql`
  mutation upsertDailyCaseQuotaOverride(
    $weeklyAvailabilityId: ID!
    $weekday: Int!
    $minCases: Int
    $maxCases: Int
  ) {
    upsertDailyCaseQuotaOverride(
      weeklyAvailabilityId: $weeklyAvailabilityId
      weekday: $weekday
      minCases: $minCases
      maxCases: $maxCases
    ) {
      id
      weeklyCaseQuota {
        totalMin
        totalMax
        baselineMin
        baselineMax
        weeklyTargetMin
        weeklyTargetMax
        targetUpdatedAt
        targetUpdatedBy {
          id
          firstname
          lastname
        }
        days {
          weekday
          minCases
          maxCases
          minSource
          maxSource
          overrideUpdatedAt
          overrideUpdatedBy {
            id
            firstname
            lastname
          }
        }
      }
    }
  }
`

export const UPSERT_WEEKLY_CASE_TARGET_MUTATION = gql`
  mutation upsertWeeklyCaseTarget(
    $weeklyAvailabilityId: ID!
    $minCases: Int
    $maxCases: Int
    $applyToProfile: Boolean
  ) {
    upsertWeeklyCaseTarget(
      weeklyAvailabilityId: $weeklyAvailabilityId
      minCases: $minCases
      maxCases: $maxCases
      applyToProfile: $applyToProfile
    ) {
      id
      weeklyCaseQuota {
        totalMin
        totalMax
        baselineMin
        baselineMax
        weeklyTargetMin
        weeklyTargetMax
        targetUpdatedAt
        targetUpdatedBy {
          id
          firstname
          lastname
        }
        days {
          weekday
          minCases
          maxCases
          minSource
          maxSource
          overrideUpdatedAt
          overrideUpdatedBy {
            id
            firstname
            lastname
          }
        }
      }
    }
  }
`

export const REVIEW_TEAM_SUBMISSION_MUTATION = gql`
  mutation reviewTeamSubmission($id: ID!, $decision: String!, $reviewNote: String) {
    reviewTeamSubmission(id: $id, decision: $decision, reviewNote: $reviewNote) {
      id
      status
      reviewedAt
      reviewNote
      reviewedBy {
        id
        firstname
        lastname
      }
    }
  }
`

export const SET_TEAM_LEADER_MUTATION = gql`
  mutation setTeamLeader($teamId: ID!, $userId: ID!) {
    setTeamLeader(teamId: $teamId, userId: $userId) {
      id
      leader {
        id
        firstname
        lastname
      }
    }
  }
`

export const REQUEST_WEEKLY_AVAILABILITIES_MUTATION = gql`
  mutation requestWeeklyAvailabilities($calendarWeek: Int!, $year: Int!, $emails: [String!]!) {
    requestWeeklyAvailabilities(calendarWeek: $calendarWeek, year: $year, emails: $emails) {
      requestedCount
      notifiedUsers {
        email
      }
    }
  }
`
